import { memo, useState, useEffect, Fragment } from 'react'
import { withStyles } from '@material-ui/styles/index'
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import classNames from 'classnames'
import identical from 'ramda/src/identical'
import map from 'ramda/src/map'
import reduce from 'ramda/src/reduce'
import filter from 'ramda/src/filter'
import pipe from 'ramda/src/pipe'
import values from 'ramda/src/values'
import last from 'ramda/src/last'
import equals from 'ramda/src/equals'
import path from 'ramda/src/path'
import { orNull, ternary, isObj } from 'root/src/shared/util/ramdaPlus'
import { universalForm } from 'root/src/client/web/componentTypes'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import SubHeader from 'root/src/client/web/typography/SubHeader'
import Header from 'root/src/client/web/typography/Header'
import Body from 'root/src/client/web/typography/Body'
import Link from 'root/src/client/web/base/Link'
import TertiaryBody from 'root/src/client/web/typography/TertiaryBody'
import StringWithLink from 'root/src/client/web/static/reusable/StringWithLink'
import submitFormHandler from 'root/src/client/logic/form/handlers/submitFormHandler'
import loadingBlockStyle from 'root/src/client/web/base/commonStyle/loadingBlockStyle'
import { errorColor, formFieldsThemes } from 'root/src/client/web/commonStyles'
import Button from 'root/src/client/web/base/Button'
import Accordion from 'root/src/client/web/base/Accordion'
import InputWrapper from 'root/src/client/web/form/InputWrapper'
import MuiStyledTextField from 'root/src/client/web/form/TextFields/MuiStyledTextField'
import hotjar from 'root/src/client/logic/analytics/hotjar'
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles'
import PayoutField from './PayoutField'
import Submits from './Submits'
import Fields from './Fields'
import SubmitError from './SubmitError'

const styles = {
	space: {
		margin: [[20, 0]],
	},
	noMarginTop: {
		marginTop: 0,
	},
	noMarginBottom: {
		marginBottom: 0,
	},
	paymentTitle: {
		marginTop: 25,
		fontSize: 32,
	},
	formContainer: {
		width: 420,
		'@media (max-width: 600px)': {
			width: 320,
		},
		'@media (max-width: 330px)': {
			padding: [[0, 20]],
		},
		paddingBottom: 30,
	},
	doubleWidth: {
		width: 840,
	},
	link: {
		textAlign: 'center',
		marginBottom: 35,
	},
	linkText: {
		textTransform: 'none',
		fontSize: 14,
		zIndex: 2,
		color: '#44d7b6',
	},
	submits: {
		margin: [[5, 'auto', 0]],
		width: 420,
		paddingBottom: 15,
		'@media (max-width: 600px)': {
			width: 320,
		},
		'@media (max-width: 330px)': {
			padding: [[0, 20]],
		},
		'& span': {
			textTransform: 'none',
		},
	},
	formTitle: {
		fontSize: 24,
	},
	errorText: {
		color: errorColor,
		margin: '1em 0',
	},
	btnsWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
	},
	flexColumn: {
		flexDirection: 'column-reverse',
		width: '100%',
	},
	cancelWrapper: {
		width: '45%',
		paddingBottom: 25,
	},
	submitWrapper: {
		width: '46%',
	},
	cancelFullWidth: {
		width: '100%',
	},
	fullWidth: {
		width: '100%',
	},
	cancelBtn: {
		backgroundColor: 'transparent',
		border: '1px solid #FFF',
		backgroundImage: 'none',
		height: 42,
		'&:hover': {
			backgroundImage: 'none',
		},
	},
	fieldsWrapper: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		'@media (max-width: 600px)': {
			minWidth: 320,
		},
	},
	fieldsWrapperColumn: {
		flexDirection: 'column',
		alignItems: 'center',
	},
	column: {
		display: 'flex',
		flexDirection: 'column',
		width: '50%',
		margin: [[0, 15]],
	},
	formWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
	},
	nowrap: {
		flexWrap: 'nowrap',
	},
	submitsRow: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '60%',
		flexWrap: 'wrap',
	},
	titles: {
		width: '100vw',
		display: 'flex',
		justifyContent: 'center',
	},
	titlesNew: {
		display: 'flex',
		textAlign: 'center',
	},
	center: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	formFullContainer: {
		width: '100%',
	},
	...loadingBlockStyle,
}

const fieldColumns = pipe(
	reduce((acc, data) => ({
		...acc,
		[last(data)]: [
			...(acc[last(data)] || []),
			data,
		],
	}), []),
	values,
)

const RenderFields = ({
	moduleKey, moduleId, classes, formFieldTypes,
	formType, wasSubmitted, doubleColumn, onBlur, onClick,
	fieldTheme, formOutputData,
}) => (
	<div className={classNames(
		classes.fieldsWrapper,
		classes.fieldsWrapperClm,
	)}
	>
		{ternary(
			identical(formType, 'payout'),
			<PayoutField
				moduleKey={moduleKey}
				moduleId={moduleId}
				formFieldTypes={formFieldTypes}
				formType={formType}
				wasSubmitted={wasSubmitted}
			/>,
			doubleColumn ? (
				<div className={classes.flexColumn}>
					<div className={classNames(
						classes.fieldsWrapper,
						classes.nowrap,
					)}
					>
						{fieldColumns(filter(type => last(type), formFieldTypes || [])).map(column => (
							<div className={classes.column} key={column[0][3]}>
								<Fields
									moduleKey={moduleKey}
									moduleId={moduleId}
									formFieldTypes={column}
									formType={formType}
									wasSubmitted={wasSubmitted}
									onBlur={onBlur}
									onClick={onClick}
									fieldTheme={fieldTheme}
								/>
							</div>
						))}
					</div>
					<Fields
						moduleKey={moduleKey}
						moduleId={moduleId}
						formFieldTypes={filter(type => !last(type), formFieldTypes)}
						formType={formType}
						wasSubmitted={wasSubmitted}
						onBlur={onBlur}
						onClick={onClick}
						fieldTheme={fieldTheme}
					/>
				</div>
			) : (
				<Fields
					moduleKey={moduleKey}
					moduleId={moduleId}
					formFieldTypes={formFieldTypes}
					formType={formType}
					wasSubmitted={wasSubmitted}
					onBlur={onBlur}
					onClick={onClick}
					fieldTheme={fieldTheme}
				/>
			),
		)}
		{formOutputData && (
			<InputWrapper
				labelFieldText="Output"
			>
				<MuiStyledTextField
					fieldValue={formOutputData}
					fieldPlaceholder="Result"
					disabled
				/>
			</InputWrapper>
		)}
	</div>
)

export const NewDesignHeader = memo(({
	classes, formTitle, formType, subTitle, fieldTheme,
}) => (
	<div className={classNames(classes.space, classes.center)}>
		{ternary(
			formTitle,
			<div
				className={classNames(
					classes.space, classes.titlesNew,
					{ [classes.noMarginTop]: (formType === universalForm) },
					'layout-row layout-align-flex-start',
				)}
			>
				<Typography
					variant={typographyVariants.heading1}
					style={{ letterSpacing: '0.02em' }}
					className={classNames(
						{ [classes.paymentTitle]: (formType === universalForm) },
					)}
				>
					{formTitle}
				</Typography>

			</div>,
			<div className={classes.space} />,
		)}
		{orNull(
			subTitle,
			<div
				className={classNames(
					classes.titlesNew, classes.subtitle,
					'layout-row layout-align-flex-start',
				)}
			>
				<SubHeader
					css={fieldTheme.formSubTitle}
				>
					<Body>
						{isObj(subTitle)
							? <StringWithLink {...subTitle} />
							: subTitle}
					</Body>
				</SubHeader>
			</div>,
		)}
	</div>
))

export const OldDesignHeader = memo(({
	classes, formTitle, formType, subTitle,
}) => (
	<Fragment>
		{ternary(
			formTitle,
			<div
				className={classNames(
					classes.space, classes.titles,
					{ [classes.noMarginTop]: (formType === universalForm) },
					'layout-row layout-align-center',
				)}
			>
				<Header
					className={classNames(
						{ [classes.paymentTitle]: (formType === universalForm) },
						classes.formTitle,
					)}
				>{formTitle}
				</Header>
			</div>,
			<div className={classes.space} />,
		)}
		{orNull(
			subTitle,
			<div
				className={classNames(
					classes.space, classes.titles,
					'layout-row layout-align-center',
				)}
			>
				<Body>
					{isObj(subTitle)
						? <StringWithLink {...subTitle} />
						: subTitle}
				</Body>
			</div>,
		)}
	</Fragment>
))

const arePropsEqual = (prevProps, nextProps) => {
	// default memo compare fn triggers rerender on every input change (formFieldTypes)
	const relevantProps = [
		'formFieldTypes', 'formTitle', 'formSubmits', 'moduleId', 'moduleKey', 'submitForm',
		'preSubmitText', 'postSubmitText', 'preSubmitCaption', 'postSubmitCaption',
		'subTitle', 'formType', 'visibleLoadingBlock', 'formSubmitError', 'onBlur', 'onClick',
		'cancelButton', 'postFormSubmits', 'submitsFullWidth', 'formFullWidth', 'doubleColumn',
		'formOutputData', 'links', 'classes', 'headerType', 'submitsRow', 'fieldSections', 'fieldTheme',
		'tabError', 'fieldSectionsHeaders', 'hotjarRecording', 'hideSubmitError', 'previousRoute',
		'formToggles', 'fieldSectionsToggles', 'setInput',
	]

	const differentProp = relevantProps.find(
		(propName) => {
			const prevElement = JSON.stringify(prevProps[propName])
			const nextElement = JSON.stringify(nextProps[propName])
			if (prevElement === undefined || nextElement === undefined) {
				return prevElement !== nextElement
			}
			const areDifferent = prevElement.localeCompare(nextElement) !== 0
			return areDifferent
		},
	)
	return !differentProp
}

const BaseForm = memo(
	({
		formFieldTypes, formTitle, formSubmits, moduleId, moduleKey, submitForm,
		preSubmitText, postSubmitText, preSubmitCaption, postSubmitCaption,
		subTitle, formType, visibleLoadingBlock, formSubmitError, onBlur, onClick,
		cancelButton, postFormSubmits, submitsFullWidth, formFullWidth, doubleColumn,
		formOutputData, links, classes, headerType, submitsRow, fieldSections, fieldTheme,
		tabError, fieldSectionsHeaders, hotjarRecording, hideSubmitError, previousRoute,
		formToggles, fieldSectionsToggles, setInput,
	}) => {
		useEffect(() => {
			if (hotjarRecording) {
				hotjar()
			}
		}, [])

		const [wasSubmitted, setWasSubmitted] = useState(false)
		const [currentAccordion, setCurrentAccordion] = useState(0)
		const submits = (
			<Submits
				moduleId={moduleId}
				moduleKey={moduleKey}
				setWasSubmitted={setWasSubmitted}
				className={submitsFullWidth && classes.marginBottom}
			/>
		)
		const setAccordionActive = index => setCurrentAccordion(index)
		return (
			<div className="flex layout-row" style={{ position: 'relative', flex: 1, padding: [[0, 20]] }}>
				{visibleLoadingBlock && (
					<div className={classNames(classes.loadingContainer, classes.loadingContainerForm)}>
						<div className={classes.loadingBlock}>
							<div className={classes.loadingText}>Loading...</div>
							<LoadingCircles />
						</div>
					</div>
				)}
				<div className={classNames(
					!formFullWidth && classes.formContainer,
					classes.center,
					classes.fullWidth,
					doubleColumn,
				)}
				>
					{headerType === 'newDesign' ? (
						<NewDesignHeader
							classes={classes}
							formTitle={formTitle}
							formType={formType}
							subTitle={subTitle}
							fieldTheme={formFieldsThemes[fieldTheme]}
						/>
					) : (
						<OldDesignHeader
							classes={classes}
							formTitle={formTitle}
							formType={formType}
							subTitle={subTitle}
							fieldTheme={formFieldsThemes[fieldTheme]}
						/>
					)}
					<div className={
						classNames(
							classes.formContainer,
							fieldSections && classes.fullWidth,
						)}
					>
						<form
							onSubmit={submitFormHandler(submitForm, moduleKey, null, setWasSubmitted)}
							className={classNames({ 'layout-column layout-align-center': (formType !== universalForm) },
								classes.formWrapper, classes.wrapper)}
						>
							{fieldSections
								? fieldSections.map((fieldSection, index) => (
									<Accordion
										label="Campaign"
										moduleKey={moduleKey}
										setInput={setInput}
										formToggles={formToggles}
										fieldSectionsToggles={fieldSectionsToggles}
										index={index}
										active={equals(index, currentAccordion)}
										setActive={setAccordionActive}
										title={fieldSection}
										key={fieldSection}
										tabError={tabError}
										fieldSectionsHeaders={fieldSectionsHeaders}
									>
										<RenderFields
											moduleId={moduleId}
											moduleKey={moduleKey}
											classes={classes}
											onClick={onClick}
											doubleColumn={doubleColumn}
											onBlur={onBlur}
											formFieldTypes={formFieldTypes[index]}
											fieldTheme={formFieldsThemes[fieldTheme]}
											formType={formType}
											wasSubmitted={wasSubmitted}
											formOutputData={formOutputData}
										/>
									</Accordion>
								))
								: (
									<RenderFields
										moduleId={moduleId}
										moduleKey={moduleKey}
										classes={classes}
										onClick={onClick}
										doubleColumn={doubleColumn}
										onBlur={onBlur}
										fieldTheme={formFieldsThemes[fieldTheme]}
										formFieldTypes={formFieldTypes}
										formType={formType}
										wasSubmitted={wasSubmitted}
										formOutputData={formOutputData}
									/>
								)
							}
							{orNull(
								preSubmitText,
								<div
									className={classNames(
										classes.span,
										'layout-row layout-align-center',
									)}
								>
									<Body>{preSubmitText}</Body>
								</div>,
							)}
							{orNull(
								preSubmitCaption,
								<div
									className={classNames(
										classes.span,
										'layout-row layout-align-center',
									)}
								>
									<TertiaryBody>{preSubmitCaption}</TertiaryBody>
								</div>,
							)}
							{orNull(formSubmits.length,
								<div
									className={classNames(
										classes.space,
										classes.submits,
										submitsRow && classes.submitsRow,
									)}
								>
									{cancelButton !== null
										? (
											<div
												className={classNames(
													classes.btnsWrapper,
													submitsFullWidth && classes.flexColumn,
												)}
											>
												<div
													className={classNames(
														submitsFullWidth
															? classes.cancelFullWidth
															: classes.cancelWrapper,
													)}
												>
													<Link
														routeId={cancelButton.routeId}
														routeParams={{ recordId: path(['routeParams', 'recordId'], cancelButton) }}
													>
														<Button className={classes.cancelBtn}>{cancelButton.label}</Button>
													</Link>
												</div>
												<div
													className={classNames(
														submitsFullWidth
															? classes.fullWidth
															: classes.submitWrapper,
														submitsRow && classes.submitsRow,
													)}
												>
													{submits}
												</div>
											</div>
										)
										: submits}
								</div>)}
							{orNull(
								postSubmitText,
								Array.isArray(postSubmitText)
									? postSubmitText.map(text => (
										<div
											className="flex layout-row layout-align-center"
											key={text}
										>
											<Body>{text}</Body>
										</div>
									))
									: (
										<div className="flex layout-row layout-align-center">
											<Body>{postSubmitText}</Body>
										</div>
									),
							)}
							{orNull(
								postSubmitCaption,
								<div className="flex layout-row layout-align-center">
									<TertiaryBody>{postSubmitCaption}</TertiaryBody>
								</div>,
							)}
							{orNull(postFormSubmits.length,
								<div
									className={classNames(
										classes.space,
										classes.submits,
										submitsRow && classes.submitsRow,
									)}
								>
									<Submits
										moduleKey={moduleKey}
										moduleId={moduleId}
										isPostFormSubmit
									/>
								</div>)}
							{orNull(
								formSubmitError && !hideSubmitError,
								<SubmitError
									formSubmitError={formSubmitError}
								/>,
							)}
							{links && (
								<div>
									{map(({ label, routeId, routeParams, dynamicPreviousRoute }) => (
										<div
											className={classes.link}
											key={routeId + label + routeParams}
										>
											{dynamicPreviousRoute ? (
												<Link
													routeId={previousRoute.routeId || routeId}
													routeParams={previousRoute.routeParams || routeParams}
												>
													<span className={classes.linkText}>{label}</span>
												</Link>
											) : (
												<Link
													routeId={routeId}
													routeParams={routeParams}
												>
													<span className={classes.linkText}>{label}</span>
												</Link>
											)}
										</div>
									), links)}
								</div>
							)}
							<input type="submit" className="hide" />
						</form>
					</div>
				</div>
			</div>
		)
	},
	arePropsEqual,
)

export default withStyles(styles, { name: 'MuiBaseForm' })(BaseForm)
